/**
 * Os dados das colunas: agenda, tarefas e conectores.
 *
 * ## Por que uma chamada só
 *
 * As três formam uma tela. Em três chamadas, a coluna da esquerda apareceria em
 * dois tempos e cada pedaço teria seu próprio estado de erro para a interface
 * tratar — três jeitos diferentes de a tela ficar meio pronta. O servidor já
 * junta em `GET {api}/painel`, e tolera a falha de cada consulta separadamente:
 * quando a agenda cai, as tarefas ainda vêm, e `falhas` diz o que faltou.
 *
 * ## Por que não fica atualizando sozinho
 *
 * Uma tela que se recarrega a cada poucos segundos gasta banco e bateria para
 * mostrar o mesmo, e pisca embaixo da mão de quem está lendo. Aqui ela busca ao
 * abrir e quando alguma coisa realmente aconteceu — o Will executou uma
 * ferramenta —, que é o único momento em que agenda e tarefas mudam por dentro
 * deste produto.
 */

import { useCallback, useEffect, useState } from 'react';
import { authFetch } from '../auth/authClient';
import { U1_API_PATH } from '../client/config';

export interface EventoDaAgenda {
  readonly id: string;
  readonly titulo: string;
  readonly inicio: string;
  readonly fim: string;
  readonly local: string | null;
  readonly participantes: readonly string[];
}

export interface TarefaNaTela {
  readonly id: string;
  readonly titulo: string;
  readonly estado: string;
  readonly prazo: string | null;
  readonly equipe: string | null;
  readonly criada_em: string;
}

export interface ConectorNaTela {
  readonly id: string;
  readonly nome: string;
  readonly estado: 'ligado' | 'sem_papel' | 'planejado';
  readonly leitura: boolean;
  readonly escrita: boolean;
  readonly ferramentas: readonly string[];
}

/**
 * Um passo do plano de uma tarefa.
 *
 * Chega achatado, com o dono em `tarefaId`: o servidor faz uma consulta só para
 * as vinte tarefas da coluna, e agrupar é trabalho de tela. Quem agrupa é o
 * `Tarefas`, uma vez por carga.
 */
export interface PassoNaTela {
  readonly id: string;
  readonly tarefaId: string;
  readonly ordem: number;
  readonly descricao: string;
  readonly tipo: 'ferramenta' | 'humano';
  readonly ferramenta: string | null;
  readonly estado: string;
  readonly resultado: string | null;
  /**
   * A execução que ficou registrada, quando ficou.
   *
   * `null` não é "não sei": é "não há nada gravado". A tela só promete
   * evidência quando existe evidência — um selo de evidência sobre nada seria
   * exatamente a mentira que o resto deste painel evita.
   */
  readonly execucaoId: string | null;
}

export interface Painel {
  readonly agenda: readonly EventoDaAgenda[];
  readonly tarefas: readonly TarefaNaTela[];
  readonly conectores: readonly ConectorNaTela[];
  readonly passos: readonly PassoNaTela[];
  readonly falhas: readonly string[];
}

const VAZIO: Painel = { agenda: [], tarefas: [], conectores: [], passos: [], falhas: [] };

/** Lê a lista de dentro do envelope do conector, sem explodir se ela faltar. */
function lista<T>(bruto: unknown, chave: string): readonly T[] {
  if (typeof bruto !== 'object' || bruto === null) return [];
  const v = (bruto as Record<string, unknown>)[chave];
  return Array.isArray(v) ? (v as T[]) : [];
}

/** Primeira das chaves que trouxer texto de verdade. Vazio conta como ausente. */
function texto(o: Record<string, unknown>, ...chaves: readonly string[]): string | null {
  for (const chave of chaves) {
    const v = o[chave];
    if (typeof v === 'string' && v !== '') return v;
  }
  return null;
}

/**
 * Os passos, um a um, descartando o que não dá para desenhar.
 *
 * Um passo sem `id` não teria como ser executado (o botão manda o id) e um sem
 * dono não teria sob qual tarefa aparecer — pular é melhor que desenhar uma
 * linha que não responde ao clique.
 *
 * `execucaoId` chegou a aceitar três grafias enquanto a rota ainda não mandava
 * o campo. A rota manda agora, e a tolerância saiu junto: três nomes para o
 * mesmo dado é a semente da próxima divergência — o dia em que o servidor
 * mudar para uma delas, a tela continua funcionando e ninguém descobre qual
 * das três é a verdadeira.
 *
 * Exportada para o teste, e não porque alguma tela precise dela: é a única
 * peça deste arquivo que decide sozinha o que aparece e o que some, e deixá-la
 * privada seria só alcançável através do `useEffect` — ou seja, montando React
 * e uma resposta HTTP de mentira para exercitar quatro `if`. O `usePainel`
 * continua sendo a porta de entrada.
 */
export function passosDe(bruto: unknown): readonly PassoNaTela[] {
  if (!Array.isArray(bruto)) return [];
  const saida: PassoNaTela[] = [];
  for (const cru of bruto) {
    if (typeof cru !== 'object' || cru === null) continue;
    const p = cru as Record<string, unknown>;
    const id = texto(p, 'id');
    const tarefaId = texto(p, 'tarefaId', 'task_id');
    if (id === null || tarefaId === null) continue;
    saida.push({
      id,
      tarefaId,
      ordem: typeof p['ordem'] === 'number' ? p['ordem'] : 0,
      descricao: texto(p, 'descricao') ?? '',
      tipo: p['tipo'] === 'humano' ? 'humano' : 'ferramenta',
      ferramenta: texto(p, 'ferramenta'),
      estado: texto(p, 'estado') ?? 'pendente',
      resultado: texto(p, 'resultado'),
      execucaoId: texto(p, 'execucaoId'),
    });
  }
  return saida;
}

export interface LeituraDoPainel {
  readonly painel: Painel;
  readonly carregando: boolean;
  /** falha da chamada inteira; falha parcial vem em `painel.falhas` */
  readonly erro: string | null;
  readonly recarregar: () => void;
}

export function usePainel(gatilho: number): LeituraDoPainel {
  const [painel, setPainel] = useState<Painel>(VAZIO);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [manual, setManual] = useState(0);

  const recarregar = useCallback(() => setManual((n) => n + 1), []);

  useEffect(() => {
    const controle = new AbortController();
    let vivo = true;

    void (async () => {
      try {
        const r = await authFetch(`${U1_API_PATH}/painel`, {
          headers: { accept: 'application/json' },
          cache: 'no-store',
          signal: controle.signal,
        });
        if (!r.ok) throw new Error(`o servidor respondeu ${r.status}`);
        const bruto: unknown = await r.json();
        if (!vivo) return;
        const d = (bruto ?? {}) as Record<string, unknown>;
        setPainel({
          agenda: lista<EventoDaAgenda>(d['agenda'], 'eventos'),
          tarefas: lista<TarefaNaTela>(d['tarefas'], 'tarefas'),
          conectores: Array.isArray(d['conectores'])
            ? (d['conectores'] as ConectorNaTela[])
            : [],
          passos: passosDe(d['passos']),
          falhas: Array.isArray(d['falhas']) ? (d['falhas'] as string[]) : [],
        });
        setErro(null);
      } catch (e) {
        if (!vivo || controle.signal.aborted) return;
        // A tela **não** é limpa no erro: mostrar o que já se sabia, mesmo
        // velho, ajuda mais que esvaziar a coluna. O aviso diz que está velho.
        setErro(e instanceof Error ? e.message : 'não consegui carregar');
      } finally {
        if (vivo) setCarregando(false);
      }
    })();

    return () => {
      vivo = false;
      controle.abort();
    };
  }, [gatilho, manual]);

  return { painel, carregando, erro, recarregar };
}
